/* oxlint-disable import/extensions */
import { icons } from '@/assets/vendor/mdi-svg.min'
import { getKPISetting } from '@/utils/config/kpis'
import { populateElementImage } from '@/utils/helpers/rappid'
import { throwBetterError } from '@/utils/helpers/throwBetterError'
import typeOf from '@/utils/helpers/typeOf'
import { $date } from '@/utils/plugins/date-fns'
import i18n from '@/utils/plugins/i18n'

const publicPath = __STATIC_IMAGES__
const publicPrefix = 'public'

/** Transforms an array of objects into an object
 *
 * @param [array] {array}     - an array of objects
 *                            @example
 *                              [
 *                                { id: '1', prop: 'xD'},
 *                                { id: '2', prop: ':P'}
 *                              ]
 * @param [options] {object}  - optional params
 *
 * @returns                   - **Object**, (with specified keys)
 *                            - _default:_
 *                                - remove 'id' key from each inner object
 *                            - _options:_
 *                                - **key** (_default='id'_) --- can be set, to specify key to enumerate upon
 *                                - **value** (_default=undefined_) --- can be set, to specify the property used to populate each key's value
 *                                - **retainKey** (_default=false_) --- can be set to true, to not remove key
 *                            @example
 *                              // default
 *                              {
 *                                '1': { prop: 'xD'},
 *                                '2': { prop: ':P'}
 *                              }
 *                            @example
 *                              // declare { key: 'prop' }
 *                              {
 *                                'xD': { id: '1' },
 *                                ':P': { id: '2' }
 *                              }
 *                            @example
 *                              // declare { value: 'prop' }
 *                              {
 *                                '1': 'xD',
 *                                '2': ':P'
 *                              }
 *                            @example
 *                              // declare { retainKey: true }
 *                              {
 *                                '1': { id: '1', prop: 'xD'},
 *                                '2': { id: '2', prop: ':P'}
 *                              }
 *                            @example
 *                              // declare { key: 'prop', retainKey: true }
 *                              {
 *                                'xD': { id: '1', prop: 'xD'},
 *                                ':P': { id: '2', prop: ':P'}
 *                              }
 */
export function arrayToObject (array, { key = 'id', value, retainKey = false } = {}) {
  if (!Array.isArray(array)) return console.error(`[arrayToObject]: was not passed an array`, array)
  if (_.isEmpty(array)) return {}
  const keyIsValid = array.every((obj) => _.hasIn(obj, key))
  const valueIsValid = array.every((obj) => _.hasIn(obj, value))
  const valuePropProvided = !!value

  const problemWith = {
    array: {
      itemsAreNotObjects: !array.every((item) => typeOf(item) === 'object'),
    },
    value: {
      keyAndValueInvalid: valuePropProvided && !keyIsValid && !valueIsValid,
      valueInvalid: valuePropProvided && keyIsValid && !valueIsValid,
    },
    key: {
      keyInvalid: !keyIsValid,
    },
  }
  if (problemWith.array.itemsAreNotObjects) return console.error(`[arrayToObject]: array items are not all objects`, { array, key })
  if (problemWith.value.keyAndValueInvalid) return console.error(`[arrayToObject]: was not passed valid key and value params`, { array, key, value })
  if (problemWith.value.valueInvalid) return console.error(`[arrayToObject]: was not passed valid value`, { array, value })
  if (problemWith.key.keyInvalid) return console.error(`[arrayToObject]: not all items have the passed key`, { array, key })

  const newObj = {}
  for (const obj of array) {
    const objKey = obj[String(key)]
    newObj[objKey] = valuePropProvided ? obj[value] : { ...obj }
    if (!retainKey) delete newObj[objKey][key]
  }
  return newObj
}

/**
 * Checks store to see if item being requested (by id) has already been fetched from the api
 * - if it has, then use the pinia store version and do not make a second call to the api
 * @param {Object.list} list  - array of available items already on the store
 * @param {Object.id} id      - id of the item to check
 * @returns {Object} { cachedItem: Object, cached: Boolean, index: Number }
 */
export function alreadyFetched ({ list, id }) {
  const index = list.findIndex(({ canonical }) => canonical === id)
  const cached = _.has(list[index], 'schema')
  const cachedItem = cached ? list.find(({ canonical }) => canonical === id) : null
  return {
    cachedItem,
    cached,
    index,
  }
}

/** Simple function to strip invalid empty properties from an object
 *
 * @param {object} [object]             any object; but optimally with all property values as objects
 * @param {regex} [.propsToRemove]      **Optional** Regex to match properties that should be removed
 *                                      - _Default_ null
 * @returns [object]                    a stripped object, with all keys that had invalid properties removed
 *                          @example [default] - removes empty/falsy properties
 *                            stripInvalidProperties({ a: {}, b: 123, c: { asd: 123 }, _d: 'abc', e: '', f: false, x: true })
                              // returns { b: 123, c: { asd: 123 }, d: 'abc', f: false }
 *
 *                          @example [propsToRemove = /(^_|x)/] - removes empty/falsy properties + props matching regex (begins with _ or is 'x')
 *                            stripInvalidProperties({ a: {}, b: 123, c: { asd: 123 }, _d: 'abc', e: '', f: false, x: true }, { propsToRemove: /(^_|x)/ })
                              // returns { b: 123, c: { asd: 123 } }
 */
export function stripInvalidProperties (obj, { propsToRemove = null } = {}) {
  const theObject = JSON.parse(JSON.stringify(obj))

  function stripProperties (object) {
    for (const key of Object.keys(object)) {
      if (propsToRemove && propsToRemove.test(key)) delete object[key]
    }

    for (const [key, value] of Object.entries(object)) {
      const isObject = typeOf(value) === 'object'
      const isArray = typeOf(value) === 'array'
      const isSet = isArray && _.every(value, _.isPlainObject)
      const hasChildren = isObject || isArray || isSet
      const deleteKey = () => {
        if (!hasChildren) return _.$isEmpty(object[key])
        if (isSet) return object[key].every((subObj) => _.every(Object.values(subObj), _.$isEmpty))
        if (isObject) return _.every(Object.values(object[key]), _.$isEmpty)
        if (isArray) return _.every(object[key], _.$isEmpty)
      }

      if (isArray) object[key] = object[key].filter((listItem) => !_.$isEmpty(listItem))
      if (isSet) object[key] = Object.keys(object[key]).map((objKey) => stripProperties(object[key][objKey]))
      if (deleteKey()) delete object[key]
      else if (hasChildren) stripProperties(object[key])
    }

    return object
  }

  return stripProperties(theObject)
}

/** Decode a base64url
 *
 * @see [RFC 4648]{@link https://tools.ietf.org/html/rfc4648#page-7}
 * @see [Wikipedia]({@link https://en.wikipedia.org/wiki/Base64}
 * Note the padding isn't required for decoding and atob works with base64
 * with padding and without
 *
 * @param {String} str  - The base64url string to decode
 * @returns {Object}    - A decoded object
 */
export function decodeJWT (str) {
  return JSON.parse(
    decodeURIComponent(
      atob(str
        .replace(/-/g, '+')
        .replace(/_/g, '/'))
        .replace(/(.)/g, (_m, char) => `%${char.charCodeAt(0).toString(16).toUpperCase().padStart(2, '0')}`),
    ),
  )
}

/** Format breadcrumbs in order to always display their label, if canonical is not provided
 *
 * @param {Object}      - An array of breadcrumbs [{ label, params, name }]
 */
const formatBreadcrumb = ({ params, name, label, query } = {}) => {
  return {
    label: label || params[Object.keys(params)[0]],
    ...(name ? { to: { name, params, query } } : {}),
    query,
  }
}

/** A helper function for breadcrumbs creation.
 *
 * @see [Github] {@link https://github.com/cycloidio/vue-chleb#using-parentslist}
 * vue-chleb expects to receive an object like this:
 *
 * {
 *   label: this.postTitle,
 *   parentsList: [
 *     {
 *       to: {
 *         name: 'category',
 *         params: {
 *           catSlug: this.$route.params.catSlug
 *         }
 *      },
 *      label: this.categoryTitle
 *    }
 *   ]
 * }
 *
 *
 * @param {String} [pageName]     - (required) The component name, to help with debugging
 * @param {String} [label]        - Label for the current route, _default_ '...'
 *                                  '...' is useful to show in place whilst something is being fetched
 * @param {Array} [parents]       - An array of parent routes, _default_ []
 *                                @example
 *                                [
 *                                  {
 *                                    label: this.$t('routes.members'),
 *                                     name: 'members'
 *                                  },
 *                                  {
 *                                    params: { orgCanonical },
 *                                    name: 'organization'
 *                                  }
 *                                ]
 *
 * @returns {Object}              - A breadcrumbs Object that can be used by vue-chleb library
 */
export function constructBreadcrumb (_pageName, label = '...', parents = []) {
  const MAX_PARENT_BREADCRUMBS = 8
  const formattedParents = _.map(parents, (breadcrumb) => formatBreadcrumb(breadcrumb))

  if (parents.length <= MAX_PARENT_BREADCRUMBS) {
    return { label, parentsList: formattedParents }
  }

  const dropdownItems = _.reverse(formattedParents.slice(MAX_PARENT_BREADCRUMBS - 1, parents.length - 1))
  const visibleParents = formattedParents.slice(0, MAX_PARENT_BREADCRUMBS - 1)

  const parentsList = [
    ...visibleParents,
    {
      ...formattedParents.at(-1),
      utils: { dropdownItems },
    },
  ]

  return { label, parentsList }
}

/** Parse given pipelines array and returns two arrays
 *
 * @param {Array} pipelines  - the pipelines to parse
 *
 * @returns {Object}          - an object containing start-stop and
 *                              non-start-stop pipelines.
 *
 *                          @example
 *                          {
 *                            startStop: [
 *                              { ... },
 *                              { ... }
 *                            ],
 *                            regular: [
 *                              { ... },
 *                              { ... },
 *                              { ... }
 *                            ],
 *                          }
 */
export function extractStartStopPipelines (pipelines = []) {
  const { true: startStop = [], false: regular = [] } = _.groupBy(pipelines, ({ name }) => {
    const isStartStop = name.startsWith('start-stop')
    const nonStartStopName = name.substring('start-stop-'.length)
    const hasMatchingNonStartStop = !_.isEmpty(pipelines.filter((pipeline) => pipeline.name === nonStartStopName))

    // This logic will avoid matching the case of non-start-stop pipelines whose names still begin with
    // `start-stop` (i.e. projects with names beginning with `start-stop`)
    return isStartStop && hasMatchingNonStartStop
  })
  return { startStop, regular }
}

/** Performs check to confirm if image exists
 *
 * @param {String} imagePath  - the path to the image
 * @param {Boolean} external  - **optional** set to true if the image is not within the codebase
 *                              _default_ = false
 * @returns `Boolean` or `resolved Promise` (_value: true or false_)
 */

export function imageExists (imagePath) {
  if (!imagePath) return false
  try {
    return !!publicPath.includes(publicPrefix + imagePath)
  } catch (error) { return false }
}

/** Displays a fullname/username/empty string based on entry params
 *
 * @param {object} [profile] - user profile object. Alternatively, an object containing
 *                             full_name or username properties

 * @returns {string} - A display name created from full_name. If this is not provided returns the username. If username is not provided returns
                        an empty string
 */
export function displayName ({ full_name: fullName, username = '' } = {}) {
  return _.every([fullName]) ? fullName : username
}

/** Returns the SVG of a MDI based on the iconName
 *
 * @param {String} [iconName] - The name of the icon
 * @param {String} [nodeId]   - The id of the svg node
 *
 * @returns {String} The SVG code of the icon
 */
export function mdiToSVG (iconName, nodeId) {
  const id = nodeId + '-svg-icon'
  return icons.svg(iconName, id)
}

/**
 * Check if a string is valid JSON
 * @param {String} str - The string to check
 * @returns {Boolean} - True if the string is valid JSON, false otherwise
 */
export function isValidJSON (str) {
  try {
    JSON.parse(str)
  } catch (e) {
    return false
  }
  return true
}

/** Check if a string is a valid JSON object with the given keys
 *
 * @param {Array} keys - The keys to check
 * @returns {Function} - A function that takes a string and returns a boolean
 */
export function hasJsonKeys (keys) {
  return (str) => {
    const parsed = JSON.parse(str)
    return _.every(keys, (key) => _.has(parsed, key))
  }
}

/** Typically used for validation checks to see if a form can save
 *
 * @param {Object} [checks]   - key-pairs: key (name of check for self-documenting code), value (a boolean logic check)
 * @returns {Boolean}
 */
export function checksPass (checks = {}) {
  if (!_.isPlainObject(checks)) return console.error(`[checksPass]: was not passed Object`, checks)
  return !_.$isEmpty(checks) && _.every(Object.values(checks))
}

/** Returns true if any of the checks pass
 *
 * @param {Object} [checks]   - key-pairs: key (name of check for self-documenting code), value (a boolean logic check)
 * @returns {Boolean}
 */
export function anyChecksPass (checks = {}) {
  if (!_.isPlainObject(checks)) return console.error(`[anyChecksPass]: was not passed Object`, checks)
  return _.some(Object.values(checks))
}

/** Joins resource-pattern canonicals with ':' after dropping trailing wildcards.
 *
 * A level left out of a resource path is a wildcard, so a trailing '*' carries no meaning and is
 * dropped — down to a single element (`*` on its own is kept). Shared by CyFormsRole and
 * CyDetailsBox so the two agree on how a pattern is normalised across add → save → reload.
 *
 * @param {string[]} [parts=[]]   - the canonical pieces, in path order
 * @returns {String}
 */
export function joinResourcePattern (parts = []) {
  const trimmed = [...parts]
  while (trimmed.length > 1 && trimmed.at(-1) === '*') trimmed.pop()
  return _.join(trimmed, ':')
}

/** Recursively searches for a property (key) in an object.
 *
 * @param {Object} object     - the object to search
 * @param {String} name       - property name we want to find
 *
 * @returns {String|null}     - A path to the property with a `this.is.a.path` syntax. Returns null if the property does not exist
 */
export function findPropertyPath (object = {}, name = '') {
  if (_.some([object, name], _.$isEmpty)) return null

  for (const property in object) {
    if (property === name) return name
    if (_.isObject(object[property])) {
      const result = findPropertyPath(object[property], name)
      if (result) return `${property}.${result}`
    }
  }
  return null
}

export function getValidOrganization (orgsList = [], routeOrgCanonical = '') {
  return _.find([
    { canonical: routeOrgCanonical },
    JSON.parse(sessionStorage.getItem(LSK.ORGANIZATION)),
    JSON.parse(localStorage.getItem(LSK.ORGANIZATION)),
    orgsList?.[0],
  ], (org) => _.find(orgsList, { canonical: org?.canonical }))
}

/** Returns an Object to populate the icon and its colour
 *
 * @param {String} status       The status returned from the getStatus endpoint
 */
export function getStatusIcon (status) {
  const placeholder = {
    icon: 'material-symbols:sensors',
    color: 'grey',
  }
  return {
    icon: {
      Success: 'fa-solid:check-circle',
      Error: 'fa-solid:exclamation-circle',
    }[status] || placeholder.icon,
    color: _.some([_.isEmpty(status), status === 'Unknown'])
      ? placeholder.color
      : status.toLowerCase(),
  }
}

/** Returns missing owner object
 *
 * @returns {object}
 */
export function getMissingOwnerObject () {
  return { username: 'no owner', isMissing: true }
}

/** Returns boolean value.
 *
 * @param {object} The owner object containing owner's username
 * @returns {boolean}
 */
export function hasNoOwner (owner = {}) {
  if (_.isString(owner) && !_.isEmpty(owner)) return false
  return owner?.isMissing || _.isEmpty(owner?.username)
}

/** Returns owner's username.
 *
 * @param {object|string} [owner={}] The owner object, or the owner's username itself.
 * @returns {string}
 */
export function getOwnerUsername (owner = {}) {
  if (hasNoOwner(owner)) return ''
  return _.get(owner, 'username', owner) ?? ''
}

/** Transforms the API pagination object to one useful for the application
 *
 * @param {Object.pagination} [original=null] From state
  *                                             - defaults to null if not supplied/original is undefined
  *                                             - { totalRecords: { fetched, available }, pagesFetched, allFetched }
 * @param {Object.pagination} pagination      From API
 *                                              - { size, total, index }
 * @returns {Object}                          { totalRecords: { fetched, available }, pagesFetched, allFetched }
 */
export function formatPagination ({ original = null, pagination }) {
  if (_.get(original, 'allFetched')) return original

  const { size, total, index } = pagination
  const pagesFetched = original
    ? [...original.pagesFetched, index]
    : [index]
  const fetched = total === size
    ? size
    : size + Number(_.get(original, 'recordsFetched', 0))
  const allFetched = total === fetched

  return {
    totalRecords: {
      fetched,
      available: total,
    },
    pagesFetched,
    allFetched,
  }
}

/** Formats number to currency string
 *
 * @param {String} Currency           'EUR'
 *
 * @returns {String}                  '€'
 */
export function getCurrencySymbol (currency = null) {
  if (!currency) return ''
  if (currency === 'CNY') return '¥'
  return new Intl.NumberFormat('en-EN', { style: 'currency', currency }).format(0).slice(0, 1)
}

/** Formats number to currency string
 *
 * @param {Number} amount           143.16
 * @param {Object} options            { currency: 'EUR', signDisplay: 'always' }
 *
 * @returns {String}                  +€143.16'
 */
export function formatAmount (amount, { currency = null, signDisplay = 'auto', decimalPlaces = 2 } = {}) {
  if (!amount && amount !== 0) return '--'
  if (isNaN(amount)) {
    console.warn('[formatAmount] passed a wrong currency shortcode')
    return ''
  }
  const formattedAmount = new Intl.NumberFormat('en-EN', {
    signDisplay,
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  }).format(amount).replace('-', '‑') // replaces hyphen with non-breaking hyphen to avoid line break in negative numbers

  const currencySymbol = getCurrencySymbol(currency)

  // we want to display costs <0.01 as <€0.01 - same for negative numbers - but zero costs as 0, no decimals
  if (amount > 0 && amount < 0.01) return `<0.01 ${currencySymbol}`
  if (amount < 0 && amount > -0.01) return `>-0.01 ${currencySymbol}`
  if (amount === 0) return `0 ${currencySymbol}`
  return `${formattedAmount} ${currencySymbol}`
}

export const getWidgetComponent = ({ type, widget }) => getKPISetting({ type, widget }).component

// True when a widget should be treated as hidden — the independent `hidden: true`
// attribute (PROD-857) or the legacy `widget: 'hidden'` type (PROD-856 / ENG-295).
export const isWidgetHidden = (widgetObj) => Boolean(widgetObj?.hidden) || widgetObj?.widget === 'hidden'

// A repeatable only counts as filled when some row holds a value: `[{}]` or rows left blank must
// not satisfy `required` (ENG-420, mirrors the backend's rowHasValue).
export const hasRepeatableValue = (rows) => _.isArray(rows) && rows.some((row) => _.some(row, (v) => !_.$isEmpty(v)))

// Whether a required field's value counts as missing, repeatable-aware.
export const isWidgetValueEmpty = (widgetObj, value) => widgetObj?.widget === 'repeatable'
  ? !hasRepeatableValue(value)
  : _.$isEmpty(value)

// True when a resolved option/value is `{label, value}` shaped rather than a bare scalar — the
// mapping a dropdown/radios/slider-list widget needs to show the label instead of the raw value.
// A caller checking a whole options list (`_.every(values, isMappedOption)`) deliberately does not
// guard the empty case: `_.every([], …)` is vacuously true, so a mapped field whose options
// haven't resolved yet still renders its `{label, value}` default as a label rather than raw JSON
// (relied on by both CyWizardStackForms' top-level `hasValueMapping(widgetObj, false)` and
// CyFormsWidgetRepeatable's row bindings; ENG-405 Bug 3).
export const isMappedOption = (item) => _.isObject(item) && !_.isArray(item) && _.$hasAll(item, ['label', 'value'])

/** Converts all values in an Object's unix timestamps from seconds to milliseconds
 *
 * Useful for when FE needs ms but BE provides s.
 */
export function convertTimeValuesToMS (object, { keys = [] } = {}) {
  if (!_.isPlainObject(object)) {
    console.error(`[convertTimeValuesToMS] was not passed an Object.`, object)
    return object
  }

  return _.transform(object, (acc, value, key) => {
    if (_.some([/.+(At|_at)$/.test(key), keys.includes(key)]) && !isNaN(Number(value))) {
      acc[key] = String(value).length === 10 ? Number(value) * 1000 : Number(value)
      return
    }
    acc[key] = value
  })
}

/** Converts all values in an Object's unix timestamps from milliseconds to seconds
 *
 * Useful for when FE needs ms but BE needs s.
 */
export function convertTimeValuesToS (object, { keys = [] } = {}) {
  if (!_.isPlainObject(object)) {
    console.error(`[convertTimeValuesToS] was not passed an Object.`, object)
    return object
  }

  return _.transform(object, (acc, value, key) => {
    if (_.some([/.+(At|_at)$/.test(key), keys.includes(key)]) && !isNaN(Number(value))) {
      acc[key] = String(value).length === 13 ? Math.floor(Number(value) / 1000) : Number(value)
      return
    }
    acc[key] = value
  })
}

/**
 * Populates each element with the correct image path
 * If the image is not accessible using the path provided, then it shall show the default image.
 * If the elements are for `vault`, then it populates each image field with the path to the vault logo.
 *
 * @param {Array} elements    - an array of elements
 * @param {String} entityType - either 'resource' or 'dataSource'
 *
 * @returns `Array`
 */
export function populateEachElementImage (elements, entityType) {
  return elements.map((element) => ({ ...element, image: populateElementImage(element, entityType) }))
}

export function hasOwnerChanged (newItem, oldItem) {
  const getOwner = ({ owner }) => hasNoOwner(owner) ? null : _.get(owner, 'username', owner)
  return !_.isEqual(getOwner(newItem), getOwner(oldItem))
}

/**
 * Returns a formatted created label
 * @param {Object} obj - The object containing the created_at timestamp
 * @returns {String} - The formatted created label. If the created_at timestamp is not available, it returns an empty string.
 */
export function getCreatedLabel (obj) {
  const created = obj.created_at || obj.createdAt
  if (_.isNil(created)) return ''
  return i18n.global.t('time.created', [$date.format($date.$getTimestamp(created), 'MMM d, yyyy')])
}

/**
 * Returns the display name of an organization's direct parent
 * @param {Object} org - The organization, carrying parent_canonical / parent_name
 * @param {Array} organizations - Known organizations, used as a name fallback
 * @returns {String|null} - The parent name, or null for a root organization
 */
export function getParentOrgName (org, organizations = []) {
  const { parent_canonical: parentCanonical, parent_name: parentName } = org || {}
  if (!parentCanonical) return null
  return parentName || _.find(organizations, ['canonical', parentCanonical])?.name || parentCanonical
}

/**
 * Returns a formatted updated label
 * @param {Object} obj - The object containing the updated_at timestamp
 * @returns {String} - The formatted updated label. If the updated_at timestamp is not available, it returns an empty string.
 */
export function getUpdatedLabel (obj) {
  const updated = obj.updated_at || obj.updatedAt
  if (_.isNil(updated)) return ''
  return i18n.global.t('time.updated', [$date.$formatTimeAgo(updated)])
}

/**
 * Returns a formatted refreshed label
 * @param {Object} obj - The object containing the refreshed_at timestamp
 * @returns {String} - The formatted refreshed label
 */
export function getRefreshedLabel (obj) {
  const refreshed = obj.refreshed_at || obj.refreshedAt
  return i18n.global.t('time.refreshed', [$date.$formatTimeAgo(refreshed)])
}

/**
 * Limits a string to a certain number of characters and adds an ellipsis if the string was truncated
 * @param {String} str - The string to limit
 * @param {Number} limit - The maximum number of characters to limit the string to
 * @returns {String} - The limited string with an ellipsis if the string is longer
 */
export function ellipsis (str, limit) {
  if (!str) return ''
  if (str.length <= limit) return str
  return `${str.substring(0, limit)}...`
}

/**
 * Syncs the browser address bar with the originally requested path stored in history state.
 * Used by error pages (403, 404) to preserve the URL the user intended to visit.
 * @param {Object} route - The current Vue Router route object ($route)
 */
export function syncAddressBarWithRequestedPath (route) {
  const { 0: requestedPath } = window.history.state || {}
  if (!_.isString(requestedPath) || !requestedPath.startsWith('/')) return
  if (route.fullPath === requestedPath) return

  window.history.replaceState(window.history.state, '', requestedPath)
}

/** Elements that a side panel's v-click-outside must not treat as outside
 * clicks: global overlays (menus, dialogs, alerts) and app-level chrome.
 * Meant as the `include` option of the directive's object form.
 *
 * @param {String[]} [extraSelectors] Additional selectors to whitelist.
 * @returns {Element[]}
 */
export function getClickOutsideExceptions (extraSelectors = []) {
  const selectors = [
    '.main-nav a',
    '.main-nav button',
    '.dev-locale-switcher__options',
    '.dev-layer',
    '.v-menu__content',
    '.cy-global-alerts',
    '.v-dialog',
    '.v-dialog__content',
    '.v-overlay',
    ...extraSelectors,
  ]
  return Array.from(document.querySelectorAll(selectors.join(', ')))
}

export {
  throwBetterError,
  typeOf,
}

export default {
  anyChecksPass,
  arrayToObject,
  checksPass,
  joinResourcePattern,
  getClickOutsideExceptions,
  constructBreadcrumb,
  convertTimeValuesToMS,
  convertTimeValuesToS,
  decodeJWT,
  displayName,
  extractStartStopPipelines,
  findPropertyPath,
  formatAmount,
  formatPagination,
  getCurrencySymbol,
  getMissingOwnerObject,
  getStatusIcon,
  getValidOrganization,
  getWidgetComponent,
  isWidgetHidden,
  hasRepeatableValue,
  isWidgetValueEmpty,
  isMappedOption,
  hasNoOwner,
  hasOwnerChanged,
  imageExists,
  mdiToSVG,
  stripInvalidProperties,
  typeOf,
  getCreatedLabel,
  getUpdatedLabel,
  getRefreshedLabel,
  ellipsis,
}
